export const M6_UNIT_11 = {
  id: "m6-u11-wishes-dreams",
  title: "Unidad 11: Wishes, Dreams & Bucket List",
  topic: "Talk about your dreams and life goals! Share what you've done and what you still want to do. Connect past experiences with future aspirations!",
  iconType: "star",
  grammarImage: "/grammar/grammar-m6-u11.jpg",
  grammarText: "I'VE NEVER...BUT I'D LIKE TO pattern. GOING TO (plans) vs WOULD LIKE (desires). HOPE vs WISH. Someday/one day time markers. Share your bucket list!",
  sets: [

    // ===================================================================
    // SET 1: BUCKET LIST FOUNDATION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s1",
      title: "Set 1: Your Bucket List",
      questions: [
        {
          type: "match",
          q: "Match bucket list vocabulary",
          pairs: [
            { left: "Bucket list", right: "Things to do before you die 🪣" },
            { left: "Life goal", right: "Important objective 🎯" },
            { left: "Dream", right: "Big aspiration 💭" }
          ],
          a: "completed",
          successtip: "✓BUCKET LIST!\nBucket list = lista antes de morir 🪣\nOrigin: 'kick the bucket' = morir (slang)\nAmerican concept, now worldwide 🌍"
        },
        {
          type: "mc",
          q: "A bucket list is:",
          options: ["Things you want to do before you die", "Things you need to buy", "Things you hate"],
          a: "Things you want to do before you die",
          tip: "BUCKET LIST = life goals/dreams",
          successtip: "✓CULTURAL!\nMovie: 'The Bucket List' (2007) 🎬\nMade this concept famous\nEveryone has one now!"
        },
        {
          type: "order",
          q: "Context: Compartiendo tu lista. Traduce: ¿Qué hay en tu bucket list?",
          words: ["What's", "on", "your", "bucket", "list?"],
          a: "What's on your bucket list?",
          tip: "ON your bucket list = en tu lista",
          successtip: "✓COMMON QUESTION!\n'What's on your bucket list?' = conversation starter 💬\nGreat for making friends\nShows interest in dreams!"
        },
        {
          type: "input",
          q: "Synonym for 'wish' or 'goal': ___ (formal word)",
          a: "aspiration",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["dream", "ambition"] },
          successtip: "✓ASPIRATION!\nAspiration = aspiración (formal) 📋\nAmbition = ambición (driven)\nDream = sueño (emotional)\nGoal = meta (specific)\nAll similar! 🎯"
        },
        {
          type: "mc",
          q: "'I've always wanted to...' shows:",
          options: ["Lifelong dream (from past until now)", "New desire", "Yesterday's thought"],
          a: "Lifelong dream (from past until now)",
          tip: "ALWAYS WANTED = desde siempre",
          successtip: "✓ALWAYS WANTED!\n'I've always wanted to...' = dream desde niño 👶\nPresent Perfect porque still true now\nEmotional phrase 💗"
        },
        {
          type: "order",
          q: "Context: Expresando deseo de vida. Traduce: Mi sueño es viajar por el mundo.",
          words: ["My", "dream", "is", "to", "travel", "the", "world."],
          a: "My dream is to travel the world.",
          tip: "MY DREAM IS TO...",
          successtip: "✓DREAM STRUCTURE!\nMy dream is to + verb ✓\nBig, emotional goal\n'Travel the world' = #1 bucket list item worldwide 🌍"
        },
        {
          type: "mc",
          q: "Completing a bucket list item: 'I've finally ___  my goal!'",
          options: ["achieved", "achieve", "achieving"],
          a: "achieved",
          tip: "ACHIEVE = lograr/alcanzar",
          successtip: "✓ACHIEVED!\nAchieve a goal = lograr 🏆\nAccomplish = similar\nFulfill = realizar (dreams)\nReach = alcanzar (targets)"
        },
        {
          type: "input",
          q: "'___ day, I'll visit Japan.' (indefinite future - algún día)",
          a: "One",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Some"] },
          successtip: "✓TIME MARKERS!\nOne day = un día 📅\nSomeday = algún día ☁️\nBoth = vague future\nPerfect for dreams without plans yet"
        },
        {
          type: "mc",
          q: "Common bucket list item: '___  the Northern Lights'",
          options: ["See", "Watch", "Look"],
          a: "See",
          successtip: "✓SEE vs WATCH!\nSee = ver (general experience) 👀\nWatch = ver (actively, TV/movie) 📺\nNorthern Lights = aurora boreal\nTop 10 bucket list item! 🌌"
        },
        {
          type: "match",
          q: "Match bucket list categories",
          pairs: [
            { left: "Travel", right: "Visit 50 countries 🌍" },
            { left: "Adventure", right: "Skydive, climb mountain 🪂" },
            { left: "Personal", right: "Learn instrument, write book 🎸" }
          ],
          a: "completed",
          successtip: "✓CATEGORIES!\nMost bucket lists have:\n🌍 Travel (most common)\n🪂 Adventure/thrills\n🎓 Learning/skills\n👨‍👩‍👧 Life milestones\n💼 Career goals"
        }
      ]
    },

    // ===================================================================
    // SET 2: I'VE NEVER...BUT I'D LIKE TO (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s2",
      title: "Set 2: Unfulfilled Dreams",
      questions: [
        {
          type: "mc",
          q: "Pattern: 'I've never been to Paris, ___ I'd like to go someday.'",
          options: ["but", "and", "or"],
          a: "but",
          tip: "NEVER...BUT (contrast)",
          successtip: "✓PERFECT PATTERN!\nI've never...BUT I'd like to 🎯\nContrast: past experience → future desire\nConnects Unit 8 + Unit 11!\nSuper natural!"
        },
        {
          type: "order",
          q: "Context: No has probado algo pero quieres. Traduce: Nunca he probado sushi, pero me encantaría probarlo.",
          words: ["I've", "never", "tried", "sushi,", "but", "I'd", "love", "to", "try", "it."],
          a: "I've never tried sushi, but I'd love to try it.",
          tip: "NEVER + PERFECT...BUT + WOULD LIKE",
          successtip: "✓NATURAL FLOW!\nPresent Perfect (experience) ✓\n→ BUT\n→ Would like (desire) ✓\nThis is how natives talk about bucket lists! 💯"
        },
        {
          type: "input",
          q: "'I've never ___ to Japan, but it's on my bucket list.'",
          a: "been",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓BEEN!\nI've been to = visited (Unit 8!) ✓\nI've gone to = ❌ (you're here!)\nAlways BEEN for travel experiences 🌍"
        },
        {
          type: "mc",
          q: "'I'd love to visit Italy someday' shows:",
          options: ["Strong desire (no specific plan yet)", "Definite plan with date", "Past action"],
          a: "Strong desire (no specific plan yet)",
          tip: "I'D LOVE TO = me encantaría",
          successtip: "✓I'D LOVE TO!\nI'd love to = me encantaría 💗\nStronger than 'I'd like to'\nBut still no concrete plan\nJust a dream 💭"
        },
        {
          type: "order",
          q: "Context: Experiencia que falta. Traduce: Todavía no he visto la aurora boreal.",
          words: ["I", "still", "haven't", "seen", "the", "Northern", "Lights."],
          a: "I still haven't seen the Northern Lights.",
          tip: "STILL HAVEN'T = todavía no (emphasis)",
          successtip: "✓STILL!\n'Still haven't' = todavía no (frustración) 😤\nEmphasis on waiting/wanting\nVs 'haven't yet' = neutral\nShows it bothers you!"
        },
        {
          type: "mc",
          q: "Completing the pattern: 'I've never skydived, ___'",
          options: ["but I want to try it one day.", "and I did it.", "because I went yesterday."],
          a: "but I want to try it one day.",
          tip: "Complete the desire pattern"
        },
        {
          type: "input",
          q: "'One ___ my goals is to learn Spanish.' (de mis metas)",
          a: "of",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ONE OF!\n'One of my goals' = una de mis metas 🎯\n'One of my dreams' = uno de mis sueños\nShows multiple goals/dreams"
        },
        {
          type: "order",
          q: "Context: Deseo futuro con ubicación. Traduce: Me gustaría vivir en el extranjero algún día.",
          words: ["I'd", "like", "to", "live", "abroad", "someday."],
          a: "I'd like to live abroad someday.",
          tip: "LIVE ABROAD = vivir en el extranjero",
          successtip: "✓ABROAD!\nAbroad = en el extranjero 🌍\nCommon life goal\nDigital nomad trend 💻\nExpat life = popular dream"
        },
        {
          type: "mc",
          q: "'I haven't learned to cook yet, but ___'",
          options: ["I plan to.", "I never will.", "I did yesterday."],
          a: "I plan to.",
          tip: "YET...BUT shows pending + intention",
          successtip: "✓YET + PLAN!\nHaven't...yet = pending (Unit 9!) ✓\nBut I plan to = future intention 📅\nShows commitment to bucket list!"
        },
        {
          type: "match",
          q: "Match unfulfilled dreams pattern",
          pairs: [
            { left: "Experience", right: "I've never been to..." },
            { left: "Desire", right: "but I'd like to..." },
            { left: "Time", right: "someday / one day" }
          ],
          a: "completed",
          successtip: "✓MASTER PATTERN!\nNEVER (past) + BUT (contrast) + WOULD LIKE (future) 🎯\nThis structure = bucket list conversations 💯\nUse constantly!"
        }
      ]
    },

    // ===================================================================
    // SET 3: GOING TO vs WOULD LIKE - CERTAINTY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s3",
      title: "Set 3: Plans vs Dreams",
      questions: [
        {
          type: "match",
          q: "Match certainty levels",
          pairs: [
            { left: "Definite plan", right: "I'm going to visit Paris 🎫" },
            { left: "Vague desire", right: "I'd like to visit Paris 💭" },
            { left: "Hopeful", right: "I hope to visit Paris 🤞" }
          ],
          a: "completed",
          successtip: "✓CERTAINTY SCALE!\nGoing to = have ticket/date 🎫\nWould like = just dreaming 💭\nHope to = realistic possibility 🤞\nWish I could = unlikely 😔"
        },
        {
          type: "mc",
          q: "'I'm going to learn Spanish next year' means:",
          options: ["Definite plan/decision made", "Vague dream", "Past action"],
          a: "Definite plan/decision made",
          tip: "GOING TO = plan definido",
          successtip: "✓GOING TO!\nGoing to = decision taken ✓\nProbably enrolled/signed up\nVs 'I'd like to' = still thinking 🤔\nCommitment level different!"
        },
        {
          type: "order",
          q: "Context: Plan definido para año próximo. Traduce: Voy a viajar a Japón el próximo año.",
          words: ["I'm", "going", "to", "travel", "to", "Japan", "next", "year."],
          a: "I'm going to travel to Japan next year.",
          tip: "GOING TO + specific time = plan"
        },
        {
          type: "input",
          q: "'I ___ like to visit Thailand someday.' (desire, no plan)",
          a: "would",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'d", "d"] },
          successtip: "✓WOULD LIKE!\nWould like = deseo sin plan 💭\nNo date, no ticket\nJust on bucket list\nMaybe someday... 🤷"
        },
        {
          type: "mc",
          q: "More certain: 'I ___ retire early.'",
          options: ["'m planning to", "'d like to", "wish I could"],
          a: "'m planning to",
          tip: "PLANNING TO = taking steps",
          successtip: "✓PLANNING!\nPlanning to = tomando acción 📋\nSaving money, making moves\nVs 'd like to = just thinking\nActions > dreams 💪"
        },
        {
          type: "order",
          q: "Context: Deseo vago sin plan. Traduce: Me gustaría aprender a tocar guitarra algún día.",
          words: ["I'd", "like", "to", "learn", "to", "play", "guitar", "someday."],
          a: "I'd like to learn to play guitar someday.",
          tip: "WOULD LIKE + SOMEDAY = vague desire",
          successtip: "✓SOMEDAY!\nSomeday = algún día (indefinido) ☁️\nNo pressure, no deadline\nPerfect with 'would like'\nBucket list language 💯"
        },
        {
          type: "mc",
          q: "'Next month' goes with:",
          options: ["I'm going to... (definite plan)", "I'd like to... (vague desire)", "I've never... (past)"],
          a: "I'm going to... (definite plan)",
          tip: "Specific time = GOING TO",
          successtip: "✓TIME MARKERS!\nSpecific (next month/year) = going to 📅\nVague (someday/one day) = would like ☁️\nTime tells certainty! ⏰"
        },
        {
          type: "input",
          q: "Turn dream into plan: 'I'd like to travel.' → 'I'm ___ to travel next year.'",
          a: "going",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓LEVEL UP!\nWould like → Going to = commitment 💪\nMoving from bucket list to calendar 📅\nThis is how dreams become reality! ✨"
        },
        {
          type: "mc",
          q: "'I'm going to start a business' suggests:",
          options: ["I've researched, made plans, ready to act", "Just a random thought", "I did it yesterday"],
          a: "I've researched, made plans, ready to act",
          successtip: "✓COMMITMENT!\nGoing to = serious intention 💼\nUsually means:\n✓ Research done\n✓ Plans made\n✓ Timeline set\nNot just dreaming anymore!"
        },
        {
          type: "match",
          q: "Match statements to certainty",
          pairs: [
            { left: "I'm traveling in July", right: "100% happening ✓" },
            { left: "I'd like to travel", right: "Maybe someday 🤷" },
            { left: "I hope to travel", right: "Want to make it happen 🤞" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 4: HOPE vs WISH - BASIC DISTINCTION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s4",
      title: "Set 4: Hope & Wishes",
      questions: [
        {
          type: "mc",
          q: "Realistic possibility: 'I ___ to travel to Europe.'",
          options: ["hope", "wish", "both OK"],
          a: "hope",
          tip: "HOPE = possible/realistic",
          successtip: "✓HOPE!\nHope = esperanza realista 🤞\nCan actually happen\nYou'll work towards it\nPositive energy! ✨"
        },
        {
          type: "mc",
          q: "Unlikely/impossible: 'I ___ I could fly.'",
          options: ["wish", "hope", "both OK"],
          a: "wish",
          tip: "WISH = unlikely/impossible",
          successtip: "✓WISH!\nWish = unlikely/imposible 😔\nCan't fly (not a bird 🐦)\nFantasy, not plan\n\n🎯 Advanced: 'I wish I were rich' (past form = unreal now)"
        },
        {
          type: "order",
          q: "Context: Esperanza realista. Traduce: Espero aprender francés.",
          words: ["I", "hope", "to", "learn", "French."],
          a: "I hope to learn French.",
          tip: "I HOPE TO + verb",
          successtip: "✓HOPE TO!\nHope to = realistic goal 🎯\nYou can do it!\nMaybe take classes, practice...\nWithin reach ✓"
        },
        {
          type: "input",
          q: "'I ___ I had more time.' (don't have, unlikely to get)",
          a: "wish",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓WISH I HAD!\n'Wish I had...' = don't have 😔\nUnlikely to change\nVs 'Hope to have' = might get ✓\n\n🎯 Advanced: 'Wish + past' = present unreal"
        },
        {
          type: "mc",
          q: "Can actually happen: 'I ___ I can finish this year.'",
          options: ["hope", "wish", "both OK"],
          a: "hope",
          successtip: "✓HOPE I CAN!\nHope I can = possibility ✓\nYou'll try your best\nRealistic expectation\nOptimism 😊"
        },
        {
          type: "order",
          q: "Context: Deseo imposible. Traduce: Ojalá pudiera viajar en el tiempo.",
          words: ["I", "wish", "I", "could", "time", "travel."],
          a: "I wish I could time travel.",
          tip: "WISH I COULD = ojalá pudiera",
          successtip: "✓WISH I COULD!\nWish I could = fantasy 🦄\nTime travel = impossible (so far!)\nUsed for impossible things\n\n🎯 'Could' after wish = unreal/impossible"
        },
        {
          type: "mc",
          q: "'I hope to buy a house' vs 'I wish I could buy a house'",
          options: ["First = possible/working towards. Second = seems impossible now.", "Both mean same thing", "First = impossible. Second = possible."],
          a: "First = possible/working towards. Second = seems impossible now.",
          successtip: "✓KEY DIFFERENCE!\nHope = realistic 💰 (saving, planning)\nWish = seems impossible 😔 (too expensive)\nSame goal, different outlook!"
        },
        {
          type: "input",
          q: "Birthday/shooting star tradition: 'Make a ___!' (deseo)",
          a: "wish",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓MAKE A WISH!\n'Make a wish' = pedir deseo ⭐\nBirthday candles 🎂\nShooting stars 💫\n11:11 on clock ⏰\nCultural tradition!"
        },
        {
          type: "mc",
          q: "Song lyrics often use:",
          options: ["'I wish...' (emotional/longing)", "'I hope...' (practical)", "Both equally"],
          a: "'I wish...' (emotional/longing)",
          successtip: "✓WISH IN SONGS!\n'I wish...' = romantic/emotional 🎵\n'Wish you were here' - Pink Floyd\n'I wish' - Stevie Wonder\nWish = sentimental feeling 💗"
        },
        {
          type: "match",
          q: "Match to correct form",
          pairs: [
            { left: "Hope", right: "I hope to travel" },
            { left: "Wish (possible)", right: "I wish to speak" },
            { left: "Wish (impossible)", right: "I wish I could fly" }
          ],
          a: "completed",
          successtip: "✓WISH FORMAL!\n'I wish to...' = formal (like 'would like') 🎩\nVs 'I wish I could...' = impossible 🚫\nDifferent structures! 📋"
        }
      ]
    },

    // ===================================================================
    // SET 5: ROLE-PLAY - DREAM CONVERSATION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s5",
      title: "Set 5: Sharing Your Dreams",
      questions: [
        {
          type: "mc",
          q: "FRIEND: 'What's on your bucket list?' YOU: '___'",
          options: ["I'd love to visit Japan someday.", "I visited Japan.", "I'm visiting now."],
          a: "I'd love to visit Japan someday.",
          tip: "Bucket list = future desires",
          successtip: "✓OPENING!\nBucket list question = sharing dreams 💭\nPerfect conversation starter\nBuild deeper friendships 🤝"
        },
        {
          type: "order",
          q: "FRIEND: 'Have you ever been skydiving?' YOU: Traduce: No, nunca lo he hecho, pero es mi sueño.",
          words: ["No,", "I've", "never", "done", "it,", "but", "it's", "my", "dream."],
          a: "No, I've never done it, but it's my dream.",
          tip: "NEVER...BUT pattern (Set 2!)",
          successtip: "✓NATURAL RESPONSE!\nI've never...but it's my dream 💭\nConnects experience + aspiration\nKeeps conversation going! 🗣️"
        },
        {
          type: "input",
          q: "FRIEND: 'Why do you want to do that?' YOU: 'It's ___ been on my bucket list.' (desde siempre)",
          a: "always",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ALWAYS!\n'Always been' = lifelong dream 👶→🧑\nPresent Perfect para continuity\nShows deep desire 💗"
        },
        {
          type: "mc",
          q: "FRIEND: 'When are you planning to go?' YOU: (no specific plan) '___'",
          options: ["Hopefully someday soon.", "Tomorrow exactly.", "I went yesterday."],
          a: "Hopefully someday soon.",
          tip: "Vague time for vague plans",
          successtip: "✓HOPEFULLY!\nHopefully = ojalá/con suerte 🤞\nSomeday soon = algún día pronto\nNo pressure timeline ☁️"
        },
        {
          type: "order",
          q: "FRIEND: 'What's your biggest dream?' YOU: Traduce: Mi mayor sueño es viajar por el mundo.",
          words: ["My", "biggest", "dream", "is", "to", "travel", "the", "world."],
          a: "My biggest dream is to travel the world.",
          tip: "BIGGEST DREAM = mayor sueño",
          successtip: "✓BIGGEST!\nBiggest dream = mayor sueño 🌍\nSuperlative question\nSharing deep aspirations 💫"
        },
        {
          type: "mc",
          q: "FRIEND: 'That's amazing! Have you started planning?' YOU: (not yet) '___'",
          options: ["Not yet, but I'm saving money.", "I've finished planning.", "I don't care."],
          a: "Not yet, but I'm saving money.",
          tip: "Show progress towards dream",
          successtip: "✓PROGRESS!\nNot yet, but... = taking steps 👣\nShows commitment\nDreams → Actions 💪"
        },
        {
          type: "input",
          q: "FRIEND: 'What inspires you?' YOU: 'I'm ___ by people who travel.' (motivated)",
          a: "inspired",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓INSPIRED!\nInspired by = inspirado por ✨\nInspire = verb\nInspired = adjective (feeling)\nInspiration = noun"
        },
        {
          type: "order",
          q: "FRIEND: 'Any other goals?' YOU: Traduce: También espero aprender otro idioma.",
          words: ["I", "also", "hope", "to", "learn", "another", "language."],
          a: "I also hope to learn another language.",
          tip: "HOPE TO = realistic goal",
          successtip: "✓ALSO!\nAlso = también (adding goals) ➕\nMost people have multiple dreams\nBucket list = plural! 📋"
        },
        {
          type: "mc",
          q: "FRIEND: 'You'll do it!' YOU: (optimistic) '___'",
          options: ["I hope so!", "I hope not.", "Maybe never."],
          a: "I hope so!",
          successtip: "✓I HOPE SO!\n'Hope so' = eso espero 🤞\nOptimistic ending\nPositive energy ✨\nVs 'I hope not' = negative"
        },
        {
          type: "mc",
          q: "FRIEND: 'What's stopping you?' YOU: (honest) '___'",
          options: ["Money and time, but I'll figure it out.", "Nothing stops me!", "I've already done it."],
          a: "Money and time, but I'll figure it out.",
          successtip: "✓OBSTACLES!\nMoney & time = universal obstacles 💰⏰\n'Figure it out' = encontrar la manera 🧩\nRealistic but determined 💪\nThis is real conversation! 💯"
        }
      ]
    },

    // ===================================================================
    // SET 6: TIME MARKERS - SOMEDAY, ONE DAY, EVENTUALLY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s6",
      title: "Set 6: Vague Future",
      questions: [
        {
          type: "match",
          q: "Match time expressions",
          pairs: [
            { left: "Someday", right: "Algún día (very vague) ☁️" },
            { left: "One day", right: "Un día (slightly more certain) 📅" },
            { left: "Eventually", right: "Eventualmente (will happen) ⏰" }
          ],
          a: "completed",
          successtip: "✓VAGUENESS LEVELS!\nSomeday = quién sabe cuándo ☁️\nOne day = sounds more committed 📅\nEventually = I'll make it happen ⏰\nAll = no specific date!"
        },
        {
          type: "mc",
          q: "'___, I'd like to retire early.'",
          options: ["Someday", "Yesterday", "Last week"],
          a: "Someday",
          tip: "SOMEDAY with desires",
          successtip: "✓SOMEDAY!\nSomeday = algún día ☁️\nNo timeline pressure\nBucket list word #1 💭\nPerfect with 'would like'"
        },
        {
          type: "order",
          q: "Context: Promesa vaga pero sincera. Traduce: Un día, escribiré un libro.",
          words: ["One", "day,", "I'll", "write", "a", "book."],
          a: "One day, I'll write a book.",
          tip: "ONE DAY + future (will/going to)",
          successtip: "✓ONE DAY!\nOne day = un día (slightly certain) 📖\nSounds like promise to yourself\nCommon with big goals\n'I'll' = determination 💪"
        },
        {
          type: "input",
          q: "'I'll learn to cook ___.' (tarde o temprano)",
          a: "eventually",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓EVENTUALLY!\nEventually = eventualmente/tarde o temprano ⏰\nWILL happen (just not sure when)\nMore certain than someday\nImplies commitment 💯"
        },
        {
          type: "mc",
          q: "Most vague time: '___'",
          options: ["Someday", "Next month", "Tomorrow"],
          a: "Someday",
          tip: "Someday = most indefinite",
          successtip: "✓COMMITMENT SCALE!\nSomeday ☁️ = dream\nOne day 📅 = intention  \nEventually ⏰ = will do\nNext month 🎫 = plan\nTomorrow ✓ = happening!"
        },
        {
          type: "order",
          q: "Context: Meta sin fecha. Traduce: Algún día, seré mi propio jefe.",
          words: ["Someday,", "I'll", "be", "my", "own", "boss."],
          a: "Someday, I'll be my own boss.",
          successtip: "✓OWN BOSS!\n'Be your own boss' = ser tu propio jefe 💼\nEntrepreneur dream común\nAmerican aspiration 🇺🇸\nFreedo-seeking mentality"
        },
        {
          type: "mc",
          q: "'In the future' vs 'someday':",
          options: ["Very similar (both vague)", "'In the future' = more specific", "'Someday' = tomorrow"],
          a: "Very similar (both vague)",
          successtip: "✓IN THE FUTURE!\nIn the future = en el futuro 🔮\nSame vagueness as someday\nSlightly more formal\nBoth = no timeline 📅"
        },
        {
          type: "input",
          q: "'I'll achieve my dreams ___.' (before dying - bucket list phrase)",
          a: "before",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["someday", "eventually"] },
          successtip: "✓BEFORE I DIE!\n'Before I die' = antes de morir 🪣\nBucket list origin phrase!\nDramatic but common\n'Before I'm too old' = similar 👴"
        },
        {
          type: "mc",
          q: "Difference: 'One day I'll travel' vs 'I'm traveling next month'",
          options: ["First = vague dream. Second = specific plan.", "Both = same thing", "First = past. Second = future."],
          a: "First = vague dream. Second = specific plan.",
          successtip: "✓TIME SPECIFICITY!\nVague time (one day) = dream 💭\nSpecific time (next month) = plan 📅\nYour language shows commitment! 💪"
        },
        {
          type: "match",
          q: "Match phrases to usage",
          pairs: [
            { left: "Someday/One day", right: "Start of sentence" },
            { left: "Eventually", right: "End of sentence" },
            { left: "In the future", right: "Middle or end" }
          ],
          a: "completed",
          successtip: "✓POSITION!\nSomeday, I'll... ✓ (beginning)\nI'll do it eventually. ✓ (end)\nI'll do it in the future. ✓ (end)\nWord order matters! 📝"
        }
      ]
    },

    // ===================================================================
    // SET 7: LIFE GOALS VOCABULARY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s7",
      title: "Set 7: Common Life Goals",
      questions: [
        {
          type: "match",
          q: "Match life goal categories",
          pairs: [
            { left: "Education 🎓", right: "Graduate, get degree, study abroad" },
            { left: "Career 💼", right: "Get promoted, start business" },
            { left: "Personal 👨‍👩‍👧", right: "Get married, have kids, buy house" }
          ],
          a: "completed",
          successtip: "✓GOAL CATEGORIES!\nMost bucket lists include:\n🎓 Education/learning\n💼 Career/money\n👨‍👩‍👧 Family/relationships\n🌍 Travel/adventure\n🎨 Hobbies/passions"
        },
        {
          type: "mc",
          q: "Complete university successfully:",
          options: ["graduate", "finish school", "end studying"],
          a: "graduate",
          tip: "GRADUATE = graduarse",
          successtip: "✓GRADUATE!\nGraduate = graduarse 🎓\nGraduate FROM university\nGraduation = ceremonia\nBig life milestone! 🎉"
        },
        {
          type: "order",
          q: "Context: Meta de estudio. Traduce: Quiero estudiar en el extranjero.",
          words: ["I", "want", "to", "study", "abroad."],
          a: "I want to study abroad.",
          tip: "STUDY ABROAD = estudiar en el extranjero",
          successtip: "✓STUDY ABROAD!\nStudy abroad = intercambio 🌍\nPopular goal (especially Europe)\nGap year alternative\nLife-changing experience ✈️"
        },
        {
          type: "input",
          q: "Career advancement: 'I hope to get ___ this year.' (ascenso)",
          a: "promoted",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["promotion", "a promotion"] },
          successtip: "✓PROMOTION!\nGet promoted = obtener ascenso 📈\nPromotion = ascenso (noun)\nCareer goal común\nMore money, more responsibility 💼"
        },
        {
          type: "mc",
          q: "Create your own company:",
          options: ["start a business", "make a business", "do a business"],
          a: "start a business",
          successtip: "✓START BUSINESS!\nStart a business = iniciar negocio 🚀\nEntrepreneur goal\nAmerican dream 🇺🇸\n'Be your own boss' 💼"
        },
        {
          type: "order",
          q: "Context: Meta familiar. Traduce: Espero tener hijos algún día.",
          words: ["I", "hope", "to", "have", "kids", "someday."],
          a: "I hope to have kids someday.",
          successtip: "✓HAVE KIDS!\nHave kids/children = tener hijos 👶\nMajor life decision\nCommon bucket list item\nTiming varies by culture 🌍"
        },
        {
          type: "mc",
          q: "Property ownership goal:",
          options: ["buy a house", "make a house", "do a house"],
          a: "buy a house",
          successtip: "✓BUY HOUSE!\nBuy a house = comprar casa 🏠\nAmerican dream classic 🇺🇸\nHuge financial goal 💰\nHomeownership = independence"
        },
        {
          type: "input",
          q: "'I'd love to ___ a language.' (become fluent)",
          a: "master",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["learn", "speak"] },
          successtip: "✓MASTER!\nMaster = dominar 🎯\nStronger than 'learn'\nFluency goal\nLearn vs Master = different levels! 💯"
        },
        {
          type: "mc",
          q: "Long-distance running challenge:",
          options: ["run a marathon", "make a marathon", "do running long"],
          a: "run a marathon",
          successtip: "✓MARATHON!\nRun a marathon = correr maratón 🏃\n42.2 km / 26.2 miles\nBucket list classic 💪\nHalf-marathon = 21 km (starter goal)"
        },
        {
          type: "match",
          q: "Match goals to verbs",
          pairs: [
            { left: "Language", right: "learn / master" },
            { left: "Instrument", right: "learn to play" },
            { left: "Book", right: "write / publish" }
          ],
          a: "completed",
          successtip: "✓CREATIVE GOALS!\nWrite a book = bucket list top 10 📖\nLearn instrument = popular (guitar #1) 🎸\nMake a difference = help others 💗\nLeave a legacy = impact world 🌍"
        }
      ]
    },

    // ===================================================================
    // SET 8: ACHIEVEMENT & PHRASAL VERBS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s8",
      title: "Set 8: Making It Happen",
      questions: [
        {
          type: "match",
          q: "Match phrasal verbs for dreams",
          pairs: [
            { left: "Work towards", right: "Trabajar hacia meta 👷" },
            { left: "Give up on", right: "Renunciar a sueño 😔" },
            { left: "Look forward to", right: "Esperar con ansias ✨" }
          ],
          a: "completed",
          successtip: "✓PHRASAL VERBS!\nWork towards = progress 📈\nGive up on = abandon 😔 (don't do this!)\nLook forward to = excited for ✨\nHold on to = mantener (dreams) 💭"
        },
        {
          type: "mc",
          q: "Successfully complete a goal:",
          options: ["achieve", "make", "do"],
          a: "achieve",
          tip: "ACHIEVE a goal",
          successtip: "✓ACHIEVE!\nAchieve = lograr 🏆\nAchieve a goal ✓\nAccomplish a dream ✓\nFulfill a wish ✓\nReach a milestone ✓\nAll similar!"
        },
        {
          type: "order",
          q: "Context: Progreso hacia meta. Traduce: Estoy trabajando hacia mi objetivo.",
          words: ["I'm", "working", "towards", "my", "goal."],
          a: "I'm working towards my goal.",
          tip: "WORK TOWARDS = trabajar hacia",
          successtip: "✓WORKING TOWARDS!\nWork towards = make progress 📈\nActive effort\nNot just dreaming anymore 💪\nAction phase! 🎯"
        },
        {
          type: "input",
          q: "'Don't ___ up on your dreams!' (abandon)",
          a: "give",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓GIVE UP!\nGive up on = renunciar 😔\nDON'T do this!\n'Never give up' = motivational phrase 💪\nPersistence = key to bucket list ✨"
        },
        {
          type: "mc",
          q: "'I finally ___  my dream!' (completed)",
          options: ["accomplished", "made", "did"],
          a: "accomplished",
          tip: "ACCOMPLISH = accomplir/lograr",
          successtip: "✓ACCOMPLISHED!\nAccomplish = lograr (formal) 🏆\nAccomplishment = logro\n'Finally' = after long wait ⏰\nCelebration word! 🎉"
        },
        {
          type: "order",
          q: "Context: Motivación. Traduce: Estoy persiguiendo mis sueños.",
          words: ["I'm", "pursuing", "my", "dreams."],
          a: "I'm pursuing my dreams.",
          tip: "PURSUE = perseguir/seguir",
          successtip: "✓PURSUE!\nPursue = perseguir (formal) 🎯\nActive chasing\nDetermined effort 💪\n'Pursuit of happiness' = American phrase 🇺🇸"
        },
        {
          type: "mc",
          q: "Reach an important point:",
          options: ["reach a milestone", "arrive a milestone", "get a milestone"],
          a: "reach a milestone",
          successtip: "✓MILESTONE!\nMilestone = hito/punto importante 🚩\nBirthdays, graduations, achievements\nMarkers on life journey 🛤️\nCelebrate milestones! 🎊"
        },
        {
          type: "input",
          q: "'I'm ___ forward to traveling!' (excited about)",
          a: "looking",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓LOOK FORWARD TO!\nLook forward to = esperar con ansias ✨\n+ verb-ING (looking forward to traveling)\nExcitement about future 😊\nPositive anticipation!"
        },
        {
          type: "mc",
          q: "Make something real:",
          options: ["make it happen", "do it happen", "create it happen"],
          a: "make it happen",
          successtip: "✓MAKE IT HAPPEN!\n'Make it happen' = hazlo realidad 💪\nMotivational phrase\nAction over talk\nBucket list motto 🎯\nJust do it! ✓"
        },
        {
          type: "match",
          q: "Match achievement stages",
          pairs: [
            { left: "Dream it", right: "Bucket list phase 💭" },
            { left: "Work towards it", right: "Action phase 💪" },
            { left: "Achieve it", right: "Success phase 🏆" }
          ],
          a: "completed",
          successtip: "✓THREE STAGES!\n1️⃣ Dream it = imagination 💭\n2️⃣ Work towards it = effort 💪  \n3️⃣ Achieve it = celebration 🏆\nBucket list journey! 🛤️"
        }
      ]
    },

    // ===================================================================
    // SET 9: INSPIRATION & MOTIVATION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s9",
      title: "Set 9: What Drives You",
      questions: [
        {
          type: "match",
          q: "Match motivation vocabulary",
          pairs: [
            { left: "Inspire", right: "Inspirar ✨" },
            { left: "Motivate", right: "Motivar 💪" },
            { left: "Encourage", right: "Alentar/animar 👍" }
          ],
          a: "completed",
          successtip: "✓MOTIVATION WORDS!\nInspire = give creative ideas ✨\nMotivate = give drive/energy 💪\nEncourage = give support 👍\nDrive = push forward 🚗\nAll help achieve dreams!"
        },
        {
          type: "mc",
          q: "Feeling from motivation:",
          options: ["I'm inspired", "I inspire", "I inspiring"],
          a: "I'm inspired",
          tip: "-ED = feeling received",
          successtip: "✓INSPIRED!\nI'm inspired = siento inspiración ✨\nI inspire others = doy inspiración 💡\nED = you receive feeling\nING = you give feeling\nImportant difference!"
        },
        {
          type: "order",
          q: "Context: Motivación personal. Traduce: Estoy motivado por mi familia.",
          words: ["I'm", "motivated", "by", "my", "family."],
          a: "I'm motivated by my family.",
          tip: "MOTIVATED BY = motivado por",
          successtip: "✓BY!\nMotivated BY = source of motivation 👨‍👩‍👧\nInspired BY = source of inspiration\nDriven BY = what pushes you\nCommon motivations: family, money, passion, legacy"
        },
        {
          type: "input",
          q: "'What ___ you?' (What pushes you forward)",
          a: "drives",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["motivates", "inspires"] },
          successtip: "✓DRIVES!\n'What drives you?' = ¿qué te impulsa? 🚗\nDeep question\nCore motivation\nInterview question común 💼"
        },
        {
          type: "mc",
          q: "Person you admire and want to be like:",
          options: ["role model", "roll model", "goal person"],
          a: "role model",
          successtip: "✓ROLE MODEL!\nRole model = modelo a seguir 🌟\nSomeone you look up to\nInspires your goals\nMentor = guides you directly 👨‍🏫\nDifferent but related!"
        },
        {
          type: "order",
          q: "Context: Admiración. Traduce: Admiro a personas que persiguen sus sueños.",
          words: ["I", "admire", "people", "who", "pursue", "their", "dreams."],
          a: "I admire people who pursue their dreams.",
          tip: "ADMIRE = admirar",
          successtip: "✓ADMIRE!\nAdmire = admirar 💫\nLook up to = admirar (phrasal)\nRespect = respetar\nAll show positive regard 👍"
        },
        {
          type: "mc",
          q: "Strong desire to achieve:",
          options: ["ambition", "ambitious", "ambitioning"],
          a: "ambition",
          tip: "AMBITION = noun",
          successtip: "✓AMBITION!\nAmbition = ambición (noun) 🎯\nAmbitious = ambicioso (adj)\n'She has great ambition' ✓\n'She's very ambitious' ✓\nDrives bucket list!"
        },
        {
          type: "input",
          q: "'I'm ___ to succeed.' (committed/resolved)",
          a: "determined",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["dedicated", "committed"] },
          successtip: "✓DETERMINED!\nDetermined = determinado 💪\nDedicated = dedicado 📚\nCommitted = comprometido 🤝\nAll show strong will!\nNeed this for bucket list ✓"
        },
        {
          type: "mc",
          q: "'My ___ is to help others.' (deep interest)",
          options: ["passion", "passionate", "passioning"],
          a: "passion",
          successtip: "✓PASSION!\nPassion = pasión (noun) 💗\nPassionate = apasionado (adj)\nWhat you love doing\nDrives best goals 🔥\n'Follow your passion' = advice común"
        },
        {
          type: "match",
          q: "Match inspiration sources",
          pairs: [
            { left: "Role models", right: "People you admire" },
            { left: "Passion", right: "What you love" },
            { left: "Purpose", right: "Why you exist" }
          ],
          a: "completed",
          successtip: "✓WHY!\nRole models = external inspiration 👥\nPassion = internal fire 🔥\nPurpose = life meaning 🎯\nAll fuel bucket lists! 💪\nFind YOUR why ✨"
        }
      ]
    },

    // ===================================================================
    // SET 10: COMPLETE INTEGRATION MASTERY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u11-s10",
      title: "Set 10: Dreams to Reality",
      questions: [
        {
          type: "mc",
          q: "Complete flow: 'Have you been to Japan?' 'No, ___'",
          options: ["I've never been there, but I'd love to go!", "I went yesterday.", "I'm there now."],
          a: "I've never been there, but I'd love to go!",
          tip: "NEVER + BUT + WOULD LIKE pattern",
          successtip: "✓MASTER PATTERN!\nQuestion (Perfect) ✓\nNever (experience) ✓\nBut (contrast) ✓\nWould like (desire) ✓\nComplete bucket list conversation! 💯"
        },
        {
          type: "order",
          q: "Context: De sueño a plan. Traduce: Antes solo era un sueño, pero ahora estoy trabajando hacia él.",
          words: ["It", "used", "to", "be", "just", "a", "dream,", "but", "now", "I'm", "working", "towards", "it."],
          a: "It used to be just a dream, but now I'm working towards it.",
          tip: "Progress narrative",
          successtip: "✓TRANSFORMATION!\nUsed to be = era antes 🕐\nBut now = pero ahora ⏰\nDream → Action 💪\nThis is GROWTH! 🌱"
        },
        {
          type: "input",
          q: "'It's been on my bucket list ___.' (desde siempre)",
          a: "forever",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["always"] },
          successtip: "✓FOREVER!\n'Forever' = desde siempre ♾️\nPresent Perfect + forever\nShows lifelong dream\nEmotional emphasis 💗"
        },
        {
          type: "mc",
          q: "Sharing bucket list: 'One of my goals ___ to...'",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'One' = singular (even though 'of my goals')",
          successtip: "✓SINGULAR!\n'One of my goals' = singular 'is' ✓\nOne (singular) takes priority\nCommon mistake to use 'are' ❌\nGrammar trap! 🪤"
        },
        {
          type: "order",
          q: "Context: Esperanza realista. Traduce: Espero lograrlo antes de los 30.",
          words: ["I", "hope", "to", "achieve", "it", "before", "I'm", "30."],
          a: "I hope to achieve it before I'm 30.",
          tip: "HOPE + specific milestone",
          successtip: "✓DEADLINE!\n'Before I'm X' = deadline personal ⏰\nAdds urgency\nMakes dream concrete 🎯\nAccountability boost! 💪"
        },
        {
          type: "mc",
          q: "'I've finally checked off ___.' (completed item)",
          options: ["a bucket list item", "a bucket item", "a list item"],
          a: "a bucket list item",
          successtip: "✓CHECK OFF!\nCheck off = tachar/completar ✅\nBucket list item\nVisual satisfaction 📝\nCrossing things off = motivating! 🎉"
        },
        {
          type: "input",
          q: "'What would you do if you could do ___?' (no limits)",
          a: "anything",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓DREAM QUESTION!\n'If you could do anything...?' 💭\nNo limits question\nReveals true dreams 🌟\nGreat conversation starter\nHelps discover bucket list! 🪣"
        },
        {
          type: "mc",
          q: "Motivational phrase: 'The only thing stopping you ___'",
          options: ["is you", "is money", "is time"],
          a: "is you",
          successtip: "✓SELF-MOTIVATION!\n'You are your only obstacle' 🪞\nMotivational cliché\nBut often true! 💯\nMindset matters 🧠\nBucket list psychology ✨"
        },
        {
          type: "order",
          q: "Context: Consejo motivacional. Traduce: No esperes, hazlo realidad.",
          words: ["Don't", "wait,", "make", "it", "happen."],
          a: "Don't wait, make it happen.",
          successtip: "✓ACTION CALL!\nDon't wait = no esperes ⏸️\nMake it happen = hazlo realidad ✓\nImperative = commanding yourself 💪\nMotivational ending! 🚀"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Most complete bucket list conversation:",
          options: ["'Have you ever skydived?' 'No, I've never done it, but it's on my bucket list. I hope to try it someday!'", "'Did you skydive?' 'No.' 'OK.'", "'Are you skydiving?' 'Maybe.'"],
          a: "'Have you ever skydived?' 'No, I've never done it, but it's on my bucket list. I hope to try it someday!'",
          successtip: "✓UNIT 11 COMPLETE! 🎉🏆\n\nMAESTRÍA DE SUEÑOS:\n✓ Bucket list vocabulary 🪣\n✓ I've never...but I'd like to (integration)\n✓ Going to vs Would like (certainty)\n✓ Hope vs Wish (possibility)\n✓ Someday/one day/eventually (time)\n✓ Life goals (education, career, personal)\n✓ Achievement vocabulary 🏆\n✓ Phrasal verbs (work towards, give up on)\n✓ Motivation words 💪\n✓ Complete dream conversations 💬\n\n✓ 100+ DREAM VOCABULARY:\n  🪣 Bucket list, life goal, dream, wish, aspiration\n  🎯 Achieve, accomplish, fulfill, reach, attain\n  ⏰ Someday, one day, eventually, in the future\n  🎓 Graduate, degree, study abroad\n  💼 Get promoted, start business\n  👨‍👩‍👧 Get married, have kids, buy house\n  🏃 Run marathon, learn language, write book\n  ✨ Inspire, motivate, encourage, drive\n  💪 Determined, dedicated, committed, passionate\n\n✓ GRAMMAR MASTERY:\n  • Present Perfect (experiences)\n  • Past Simple (specific times)\n  • Going to (definite plans)\n  • Would like (vague desires)\n  • Hope to (realistic)\n  • Wish I could (unlikely)\n\n✓ CONVERSATION FLOW:\nQuestion → Never → But → Would like → Hope → Plan\n\nPuedes ahora:\n💬 Share your bucket list\n🎯 Distinguish plans from dreams\n⏰ Use vague time markers\n💪 Talk about motivation\n✨ Inspire others\n🏆 Discuss achievements\n🚀 Make it happen!\n\n¡B2 LEVEL INSPIRATIONAL!\n¡You're ready to chase YOUR dreams! 🌟\n\nMódulo 6: 11/12 units complete! 🚀\n¡Solo falta la ÚLTIMA! 💪"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - BUCKET LIST BLOG (10 preguntas)
  // ===================================================================
  readingSet: {
    id: "m6-u11-reading",
    title: "Reading Practice - My Bucket List Journey",
    category: "reading",
    
    questions: [
      // BLOQUE 1: Introduction
      {
        type: "passage",
        passage: `**LifeGoalsAdventures Blog**
**Post: My Bucket List - Dreams to Reality**
*By Alex Chen | January 2026*

Three years ago, I created my first bucket list. I'd always wanted to do so many things, but I'd never written them down. One day, I decided to make it official. I grabbed a notebook and wrote down everything I'd ever dreamed of doing.

The list was long – 50 items! Some were big dreams like "travel to all seven continents," and others were smaller like "learn to make pasta from scratch." Looking at the list, I felt both excited and overwhelmed. How would I ever accomplish all of this?

But then I remembered something my grandmother used to say: "A dream written down becomes a goal. A goal broken down becomes a plan. A plan backed by action makes your dream come true." So I decided to start working towards these dreams, one at a time.`
      },
      {
        type: "mc",
        q: "Why does Alex use 'I'd always wanted' (Present Perfect)?",
        options: ["Lifelong desire that continued until the past moment", "One specific time", "Future plan"],
        a: "Lifelong desire that continued until the past moment",
        tip: "'d always wanted = había querido siempre"
      },
      {
        type: "mc",
        q: "What does 'bucket list' mean in this context?",
        options: ["Things you want to do before you die", "Shopping list", "Work tasks"],
        a: "Things you want to do before you die",
        successtip: "✓BUCKET LIST BLOG!\nWriting down dreams = first step 📝\nMakes them real ✓"
      },

      // BLOQUE 2: Completed Items
      {
        type: "passage",
        passage: `**What I've Accomplished So Far:**

I'm proud to say I've checked off 15 items from my original list! Here are some highlights:

✅ **Learned to scuba dive** - I'd never been underwater with equipment before, but I'd always been fascinated by the ocean. Last year, I got certified in Thailand. It was incredible! I've been diving five times since then.

✅ **Ran my first 5K** - I've never been athletic, but I'd always wanted to prove to myself I could do it. I trained for three months and finally finished a race in October. I'm now planning to run a 10K!

✅ **Traveled solo** - I'd always traveled with friends or family, so this was scary. But I went to Iceland alone for a week. It was one of the best experiences of my life. I've become so much more confident since then.

✅ **Learned to cook my grandmother's recipe** - She'd always made the best dumplings, and I'd always meant to learn how. I finally asked her to teach me before she passed away last year. Now I can make them, and it makes me feel connected to her.`
      },
      {
        type: "mc",
        q: "For scuba diving, Alex uses 'I'd never been' (Past Perfect). Why?",
        options: ["Talking about experience before another past moment (before getting certified)", "Present action", "Future plan"],
        a: "Talking about experience before another past moment (before getting certified)",
        tip: "Past Perfect = before another past time"
      },
      {
        type: "mc",
        q: "'I've checked off' shows:",
        options: ["Completed items (from past until now)", "Will complete", "Checking now"],
        a: "Completed items (from past until now)",
        successtip: "✓CHECK OFF!\nCheck off = tachar ✅\nCompleted bucket list items\nPresent Perfect porque still relevant 💯"
      },

      // BLOQUE 3: Still Pending
      {
        type: "passage",
        passage: `**What's Still on My List:**

There are 35 items I haven't accomplished yet, but I'm working towards many of them:

**❌ Visit all seven continents** - I've been to four so far (Asia, Europe, North America, and Australia). I still haven't been to South America, Africa, or Antarctica. But I'm planning to visit Peru next year! Antarctica is the most expensive, so I'm saving money for that one. Someday, I hope to check this off.

**❌ Learn another language** - I've always wanted to be bilingual, but I haven't committed to studying seriously yet. I've tried apps, but I need to be more dedicated. This year, I'd like to take a proper Spanish class.

**❌ Write a book** - This has been on my list since I was a teenager. I've started writing several times, but I've never finished. One day, I'll do it. I know I will eventually.

**❌ Learn to play guitar** - I still haven't learned an instrument. I'd love to be able to play songs around a campfire! I hope to start lessons this spring.`
      },
      {
        type: "mc",
        q: "Why 'I haven't accomplished yet' (not 'I didn't accomplish')?",
        options: ["Still have time to do them (ongoing list)", "Talking about specific past time", "They'll never happen"],
        a: "Still have time to do them (ongoing list)",
        tip: "YET shows still possible"
      },
      {
        type: "mc",
        q: "'Someday, I hope to...' vs 'I'm planning to visit Peru next year':",
        options: ["First = vague dream. Second = definite plan.", "Both = same certainty", "First = more certain"],
        a: "First = vague dream. Second = definite plan.",
        successtip: "✓CERTAINTY!\nSomeday = vague ☁️\nNext year = specific plan 📅\nLanguage shows commitment! 💪"
      },

      // BLOQUE 4: Plans & Intentions
      {
        type: "passage",
        passage: `**My Goals for This Year:**

I'm going to focus on three specific items from my bucket list this year:

1. **Visit South America** - I've already bought my ticket to Peru for June! I'm going to hike the Inca Trail to Machu Picchu. I've been training by hiking every weekend. I can't wait!

2. **Take a Spanish class** - I've signed up for classes starting next month. I'd like to be conversational before my Peru trip. I'm motivated because I'll actually use it!

3. **Run a 10K race** - I'm training now. The race is in May. I'm determined to finish it, even if I have to walk part of it. I've learned that progress matters more than perfection.

I've learned that the key to achieving bucket list dreams is breaking them into smaller, specific goals. "Travel the world" is overwhelming, but "visit Peru in June" is achievable. I'm making it happen, one dream at a time!`
      },
      {
        type: "mc",
        q: "Why 'I'm going to hike' (not 'I'd like to hike')?",
        options: ["Definite plan (has ticket, training)", "Just a dream", "Past action"],
        a: "Definite plan (has ticket, training)",
        successtip: "✓GOING TO!\nGoing to = committed 🎫\nAlready taking action (training)\nNot just dreaming! 💪"
      },
      {
        type: "mc",
        q: "'I've signed up' shows:",
        options: ["Recent action that's relevant now", "Action yesterday", "Future action"],
        a: "Recent action that's relevant now",
        tip: "Present Perfect for recent completion"
      },

      // BLOQUE 5: Encouragement (CONTINUACIÓN)
      {
        type: "passage",
        passage: `**My Advice to You:**

If you haven't made a bucket list yet, I encourage you to start today. Here's what I've learned:

1. **Write it down** - I've always had dreams, but writing them down made them real. They became goals instead of just wishes.

2. **Start small** - I've never climbed Mount Everest, but I have climbed smaller mountains. Every step counts!

3. **Don't wait** - I'd always thought "I'll do it when I have more time/money." But the truth is, there's never a perfect time. Start now with what you have.

4. **Celebrate progress** - I've learned to celebrate checking off items, even the small ones. Each accomplishment motivates me to keep going.

5. **Stay flexible** - Some dreams change. I've removed items from my list and added new ones. That's OK! Your bucket list should grow with you.

**Final Thoughts:**

Three years ago, my bucket list was just a dream. Today, it's becoming my reality. I've accomplished things I never thought possible. I still haven't achieved everything, but I'm working towards it every day.

What's on YOUR bucket list? I'd love to hear about your dreams in the comments below!

Remember: Someday is not a day of the week. If you've always wanted to do something, start today. One day, you'll look back and be proud you did.

**— Alex Chen**
*Living the dream, one bucket list item at a time 🪣✨*`
      },
      {
        type: "mc",
        q: "Alex says 'If you haven't made a bucket list yet...' - why 'yet'?",
        options: ["You still have time to do it (encouraging)", "You'll never do it", "You did it yesterday"],
        a: "You still have time to do it (encouraging)",
        successtip: "✓YET = HOPE!\nYet = todavía hay tiempo ⏰\nEncouraging readers\nNot too late to start! 💪"
      },
      {
        type: "mc",
        q: "'Someday is not a day of the week' means:",
        options: ["Stop waiting, take action now", "Someday = Sunday", "Wait for perfect time"],
        a: "Stop waiting, take action now",
        successtip: "✓POWERFUL PHRASE!\n'Someday' = excuse ❌\nAction TODAY = success ✓\nMotivational message 💪\nStop procrastinating!"
      },
      {
        type: "mc",
        q: "Throughout the blog, Alex uses Present Perfect to:",
        options: ["Connect past experiences to present reality", "Talk about specific dates", "Describe future plans"],
        a: "Connect past experiences to present reality",
        successtip: "✓PERFECT USAGE!\nI've accomplished = vida hasta ahora 📊\nI've learned = knowledge now 🧠\nI've been = experiences count 🌍\nThis is Present Perfect mastery! 💯"
      },
      {
        type: "mc",
        q: "The blog structure shows:",
        options: ["Past (never/always wanted) → Present (accomplished) → Future (plans)", "Only past", "Only future"],
        a: "Past (never/always wanted) → Present (accomplished) → Future (plans)",
        successtip: "✓TIMELINE!\nPast desires 💭\n↓\nPresent achievements 🏆\n↓\nFuture plans 🚀\n\nThis is bucket list journey! 🛤️"
      },
      {
        type: "mc",
        q: "FINAL: This blog post teaches:",
        options: ["How to turn dreams into reality using all verb tenses naturally", "Only vocabulary", "Only grammar rules"],
        a: "How to turn dreams into reality using all verb tenses naturally",
        successtip: "✓READING COMPLETE! 🎉📖\n\nGRAMMAR PATTERNS SEEN:\n✅ Present Perfect:\n  • I've accomplished (achievements)\n  • I've always wanted (lifelong dreams)\n  • I've checked off (completed items)\n  • I haven't...yet (pending)\n✅ Past Simple:\n  • I got certified (specific time)\n  • I went to Iceland (specific trip)\n✅ Going to:\n  • I'm going to hike (definite plan)\n✅ Would like:\n  • I'd like to be conversational (desire)\n✅ Hope:\n  • I hope to check this off (realistic)\n✅ Time markers:\n  • Someday, one day, eventually, next year\n\nVOCABULARY FOUND:\n🪣 Bucket list, life goal, dream, aspiration\n🏆 Accomplish, achieve, check off\n💪 Working towards, committed, determined\n⏰ Someday, one day, eventually\n✨ Motivated, inspired, confident\n\nKEY MESSAGES:\n💡 Write it down = make it real\n💡 Start small = progress counts\n💡 Don't wait = act now\n💡 Celebrate wins = stay motivated\n💡 Stay flexible = evolve\n💡 'Someday' isn't real = TODAY is\n\nBUCKET LIST CATEGORIES:\n🌍 Travel (continents)\n🏃 Fitness (races)\n🎓 Learning (language, instrument)\n👨‍👩‍👧 Personal (family recipes)\n🎨 Creative (write book)\n🪂 Adventure (scuba dive)\n\n✨ INSPIRATIONAL LESSON:\nDreams → Written goals → Plans → Action → Reality\n\n¡Complete bucket list blog understood! 🌟\n¡Listo para crear TU bucket list! 🪣💪"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (10 preguntas)
  // ===================================================================
  reviewSet: {
    id: "m6-u11-review",
    title: "Unit Review - Dreams Mastery",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "REVIEW: Pattern for unfulfilled desire:",
        options: ["I've never been there, but I'd like to go", "I never was there, but I want go", "I didn't be there, but I going"],
        a: "I've never been there, but I'd like to go",
        tip: "NEVER + BUT + WOULD LIKE"
      },
      {
        type: "mc",
        q: "REVIEW: Definite plan:",
        options: ["I'm going to travel next year", "I'd like to travel someday", "I wish I could travel"],
        a: "I'm going to travel next year",
        tip: "GOING TO with specific time"
      },
      {
        type: "input",
        q: "REVIEW: Realistic possibility - 'I ___ to learn Spanish.' (espero)",
        a: "hope",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "HOPE = realistic"
      },
      {
        type: "mc",
        q: "REVIEW: Vague future time:",
        options: ["someday / one day", "yesterday", "tomorrow"],
        a: "someday / one day",
        tip: "Someday = algún día"
      },
      {
        type: "mc",
        q: "REVIEW: Successfully complete a goal:",
        options: ["achieve", "make", "do"],
        a: "achieve",
        tip: "ACHIEVE a goal"
      },
      {
        type: "order",
        q: "REVIEW: Traduce - ¿Qué hay en tu bucket list?",
        words: ["What's", "on", "your", "bucket", "list?"],
        a: "What's on your bucket list?"
      },
      {
        type: "mc",
        q: "REVIEW: Work towards vs give up on:",
        options: ["First = progress. Second = abandon.", "Both = same thing", "First = abandon. Second = progress."],
        a: "First = progress. Second = abandon.",
        tip: "Work towards ✓ Give up on ❌"
      },
      {
        type: "match",
        q: "REVIEW: Match certainty levels",
        pairs: [
          { left: "Going to", right: "Definite plan 🎫" },
          { left: "Would like", right: "Vague desire 💭" },
          { left: "Hope to", right: "Realistic possibility 🤞" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "REVIEW: Lifelong dream phrase:",
        options: ["I've always wanted to...", "I always want to...", "I wanted always to..."],
        a: "I've always wanted to...",
        tip: "Always with Present Perfect"
      },
      {
        type: "mc",
        q: "FINAL REVIEW: Most natural bucket list conversation:",
        options: ["'Have you ever traveled solo?' 'No, I've never done it, but I'd love to someday. It's on my bucket list!'", "'Did you travel solo?' 'No.' 'OK.'", "'Are you traveling solo?' 'Maybe tomorrow.'"],
        a: "'Have you ever traveled solo?' 'No, I've never done it, but I'd love to someday. It's on my bucket list!'",
        successtip: "✓UNIT 11 MASTERY COMPLETE! 🎉🏆\n\nDOMINIO TOTAL:\n✅ Bucket list vocabulary 🪣\n✅ Never...but pattern integration\n✅ Going to vs Would like\n✅ Hope vs Wish distinction\n✅ Time markers (someday/one day)\n✅ Life goals vocabulary\n✅ Achievement verbs\n✅ Phrasal verbs for dreams\n✅ Motivation vocabulary\n✅ Complete conversations\n✅ 100+ dream vocabulary\n✅ Inspirational mindset\n\n💭 PAST → PRESENT → FUTURE\nExperiences → Dreams → Plans → Reality\n\n🌟 ¡BUCKET LIST MASTER!\n💯 B2 Level inspirational!\n🎯 Ready to achieve YOUR dreams!\n\nMódulo 6: 11/12 complete! 🚀\n\n¿Lista para la ÚLTIMA unidad? 🏁✨"
      }
    ]
  }
};
